import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProjectView } from '@lemoncloud/codes-backend-api';
import { Params } from '@lemoncloud/lemon-web-core';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { PaginationType, useCustomMutation } from '@codes/shared';

import { createProject, deleteProject, fetchProjectById, fetchProjectsByWorkspaceId, updateProject } from '../apis';
import { CreateProjectDTO, UpdateProjectDTO } from '../types';

export const projectsKeys = {
    all: ['projects'] as const,
    lists: () => [...projectsKeys.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...projectsKeys.lists(), { filters }] as const,
    details: () => [...projectsKeys.all, 'detail'] as const,
    detail: (id: string) => [...projectsKeys.details(), id] as const,
} as const;

export const useProjects = (workspaceId: string, params: Params = {}) =>
    useQuery<PaginationType<ProjectView[]>>({
        queryKey: projectsKeys.list({ workspaceId, ...(params && { params }) }),
        queryFn: async () => {
            const result = await fetchProjectsByWorkspaceId(workspaceId, params);
            return { ...result, data: result.list || [] } as PaginationType<ProjectView[]>;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: !!workspaceId,
    });
export const useProject = (projectId: string) =>
    useQuery<ProjectView>({
        queryKey: projectsKeys.detail(projectId),
        queryFn: () => fetchProjectById(projectId),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: !!projectId,
    });

export const useCreateProject = ({ duplicatedErrorMsg }: { duplicatedErrorMsg: string }) => {
    return useCustomMutation(
        async (data: CreateProjectDTO) => {
            const project = await createProject(data);

            return project;
        },
        {
            onError: error => {
                const axiosError = error as AxiosError<any>;

                if (axiosError?.response?.data?.includes('duplicated')) {
                    toast({ title: duplicatedErrorMsg });
                    return;
                }

                toast({ title: `${error?.toString()}` });
            },
        }
    );
};

export const useUpdateProject = () => {
    return useCustomMutation((data: UpdateProjectDTO) => updateProject(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useDeleteProject = () => {
    return useCustomMutation((id: string) => deleteProject(id), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};
